import React from 'react';
import ReactDOM from 'react-dom';
import styled, { useTheme } from 'styled-components/macro';
import { AnimatePresence, motion } from 'framer-motion';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils';
import { IconWrapper, IconX } from '../../icons';

const TooltipContainerStyled = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: ${SPACINGS[4]};
  right: ${SPACINGS[4]};
  box-shadow: ${(p) => p.theme.boxShadows.sm};
  border-radius: 4px 4px 0 0;
  overflow: hidden;
  z-index: ${(p) => p.theme.zIndexes.popup};
  background-color: ${(p) => p.theme.colors.white};

  .close-button {
    position: absolute;
    top: ${SPACINGS[4]};
    right: ${SPACINGS[4]};
  }

  .image {
    z-index: 1;
    position: absolute;
    bottom: 0;
    right: 0;
    pointer-events: none;
  }
`;

const TooltipStyled = styled.div`
  position: relative;
  z-index: 2;
  padding: ${SPACINGS[4]};
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const variants = {
  isOpen: { opacity: 1, height: 'auto' },
  closed: { opacity: 0, height: 0 },
};

export type MobileTooltipProps = {
  isOpen: boolean;
  onChange: (v: boolean) => void;
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  text?: string | React.ReactNode;
  image?: string;
  containerStyle?: React.CSSProperties;
  tooltipStyle?: React.CSSProperties;
  tooltipContainerStyle?: React.CSSProperties;
  imageStyle?: React.CSSProperties;
};

function MobileTooltip(props: MobileTooltipProps) {
  const {
    children,
    title,
    text,
    image,
    containerStyle,
    tooltipStyle,
    tooltipContainerStyle,
    imageStyle,
    isOpen,
    onChange,
  } = props;
  const theme = useTheme();

  function handleClick(e: React.MouseEvent, value: boolean) {
    e.stopPropagation();
    if (onChange) onChange(value);
  }

  return (
    <>
      {children && (
        <div style={containerStyle} onClick={(e) => handleClick(e, !isOpen)}>
          {children}
        </div>
      )}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {isOpen && (
            <TooltipContainerStyled
              initial="closed"
              animate="isOpen"
              exit="closed"
              variants={variants}
              style={tooltipContainerStyle}
            >
              <TooltipStyled style={tooltipStyle}>
                <div className="close-button">
                  <IconWrapper
                    type="action"
                    onClick={(e) => handleClick(e, false)}
                  >
                    <IconX stroke={theme.colors.primary20b} />
                  </IconWrapper>
                </div>
                {typeof title === 'string' ? (
                  <Text margin="none" style={{ marginRight: SPACINGS[8] }}>
                    {title}
                  </Text>
                ) : (
                  title
                )}
                {typeof text === 'string' ? (
                  <Text size="sm" type="label" margin="none" innerHTML={text} />
                ) : (
                  text
                )}
              </TooltipStyled>
              {image && (
                <img
                  style={imageStyle}
                  className="image"
                  alt="background"
                  src={image}
                />
              )}
            </TooltipContainerStyled>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

export default MobileTooltip;
